import AndieGenericToolRender from './AndieGenericToolRender';
import { useTranslation } from 'react-i18next';
import { ActionRenderAi } from '@/ai/types/action-render-ai';
import { ActionWorkflowAi } from '@/ai/types/action-workflow-ai';
import { useMemo, useEffect, useRef, useState } from 'react';
import { AutoAwesomeRounded, InfoRounded } from '@mui/icons-material';
import { Alert, CardContent, Divider } from '@mui/material';
import { ActionResponseStatusAi } from '@/ai/enum/action-response-status-ai';
import { useAndie } from '@/providers/andie-provider';
import { useSnackbar } from 'notistack';
import { useQuery } from '@apollo/client';
import { PostsByIdsDocument } from '@/graphql/generated/posts-by-ids.generated';

type ActionResponse = ActionWorkflowAi<{
    postIds: string[];
    failures?: {
        postId: string;
        reason?: string;
    }[];
}>;

type ActionArgs = {
    postIds: string[];
    generateCaptions?: boolean;
    generateMedias?: boolean;
};

export type AndieBulkBriefingContentWorkflowProps = ActionRenderAi<ActionResponse, ActionArgs>;

const AndieBulkBriefingContentWorkflow = (props: AndieBulkBriefingContentWorkflowProps) => {
    const { t, i18n } = useTranslation();
    const listFormatter = useMemo(
        () => new Intl.ListFormat(i18n.language, { type: 'conjunction' }),
        [i18n.language],
    );
    const { helpers } = useAndie();
    const { enqueueSnackbar } = useSnackbar();
    const executingSteps = useRef<number>(0);
    const [postIds, setPostIds] = useState<string[]>([]);

    useQuery(PostsByIdsDocument, {
        skip: postIds.length === 0,
        variables: { ids: postIds },
        onCompleted: (data) => {
            data?.postsByIds?.forEach((post) => helpers.updatePost(post));
        },
        onError: (e) => {
            enqueueSnackbar(e.message);
        },
        fetchPolicy: 'network-only',
    });

    useEffect(() => {
        if (props.status === ActionResponseStatusAi.EXECUTING) {
            executingSteps.current += 1;
        }

        if (
            props.status === ActionResponseStatusAi.COMPLETE &&
            props.result?.result?.postIds?.length &&
            executingSteps.current > 0
        ) {
            setPostIds(props.result.result.postIds);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.status]);

    return (
        <AndieGenericToolRender<ActionResponse, ActionArgs>
            title={() => t('andie.tool.bulk-briefing-content.title')}
            icon={<AutoAwesomeRounded fontSize="small" />}
            loadingMessages={(args) => [
                t('andie.tool.bulk-briefing-content.loading', {
                    value: args?.postIds?.length || 0,
                    generationList: listFormatter.format([
                        ...(args
                            ? [
                                  ...(args?.generateCaptions ? [t('andie.tool.post.bodies')] : []),
                                  ...(args?.generateMedias ? [t('andie.tool.post.medias')] : []),
                              ]
                            : []),
                    ]),
                }),
            ]}
            completeMessage={({ result, args }) => {
                const successCount = result?.result?.postIds?.length || 0;
                const failureCount = result?.result?.failures?.length || 0;
                const generationList = listFormatter.format([
                    ...(args
                        ? [
                              ...(args?.generateCaptions ? [t('andie.tool.post.bodies')] : []),
                              ...(args?.generateMedias ? [t('andie.tool.post.medias')] : []),
                          ]
                        : []),
                ]);

                if (failureCount > 0) {
                    return t('andie.tool.bulk-briefing-content.complete-partial', {
                        defaultValue:
                            'Generated {{generationList}} for {{value}} posts. {{failed}} posts need a retry.',
                        value: successCount,
                        failed: failureCount,
                        generationList,
                    });
                }

                return t('andie.tool.bulk-briefing-content.complete', {
                    value: successCount,
                    generationList,
                });
            }}
            additionalComponent={({ result }) => {
                const failureCount = result?.result?.failures?.length || 0;
                return (
                    <>
                        <Divider />
                        <CardContent>
                            <Alert
                                icon={<InfoRounded fontSize="inherit" />}
                                severity={failureCount > 0 ? 'warning' : 'info'}
                            >
                                {failureCount > 0
                                    ? t('andie.tool.bulk-briefing-content.partial-alert', {
                                          defaultValue:
                                              'Some posts could not be completed this time. The posts that were updated are already saved, and the remaining ones can be retried.',
                                      })
                                    : t('andie.tool.bulk-briefing-content.alert')}
                            </Alert>
                        </CardContent>
                    </>
                );
            }}
            {...props}
        />
    );
};

export default AndieBulkBriefingContentWorkflow;
